/**
 * Painting store (Zustand): the client-side source of truth for the plan
 * being drawn.
 *
 * Responsibilities:
 *  - Hold the active plan's block→district assignment map (GEOID -> index).
 *  - Hold block populations (fetched ONCE per session) + derived totals.
 *  - Undo/redo stacks of assignment diffs (inverse ops, capped at 100).
 *  - Dirty tracking + debounced bulk-upsert persistence (PUT assignments).
 *
 * The store never touches the map directly. The painting tool
 * (src/map/tools/painting.ts) registers a MapBridge so undo/redo/plan-load
 * can push feature-state updates; the tool itself writes feature-state
 * during strokes for the 60fps path.
 *
 * Persistence semantics: only blocks changed since the last successful save
 * are sent (dirtyGeoids). A failed save keeps the dirty set and flips
 * saveState to 'error' with a retry affordance — assignments are never
 * silently dropped.
 */
import { create } from 'zustand';
import type { TenantConfig } from '../lib/api';
import { resolveDistrictColors, resolveDistrictLabels } from '../lib/districtColors';
import {
  deleteLocalDraft,
  isLocalPlanId,
  listLocalDrafts,
  readLocalDraft,
  writeLocalDraft,
} from '../lib/localDrafts';
import {
  createPlan,
  deletePlan,
  DISTRICT_LABEL_MAX,
  fetchBlockPopulations,
  getPlan,
  mergeDistrictLabels,
  putDistrictLabels,
  putAssignments,
  putPlanComments,
  type PlanDetail,
  type PlanStatus,
  type PlanSummary,
  type ValidationReport,
} from '../lib/plans';

/** One block's assignment change within a stroke (or undo/redo op). */
export interface AssignmentChange {
  geoid: string;
  prev: number | null;
  next: number | null;
}

/** A committed stroke: the full diff plus its inverse (prev values). */
export interface StrokeDiff {
  changes: AssignmentChange[];
}

export type SaveState = 'idle' | 'saving' | 'error';
export type CommentsState = 'idle' | 'pending' | 'saving' | 'saved' | 'error';
/** Debounce for comment autosave (typing pauses). */
export const COMMENTS_SAVE_DEBOUNCE_MS = 900;
export type PaintMode = 'paint' | 'pan' | 'erase';
/** Brush radius steps: 0 = single block, then growing circular brushes. */
export type BrushSize = 0 | 1 | 2 | 3;
/** Brush radius in CSS pixels per BrushSize step. */
export const BRUSH_RADIUS_PX: Record<BrushSize, number> = { 0: 0, 1: 14, 2: 30, 3: 56 };
export const BRUSH_LABELS: Record<BrushSize, string> = { 0: 'Single block', 1: 'Small', 2: 'Medium', 3: 'Large' };
export type PopulationsState = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Narrow bridge to the live MapLibre map, registered by the painting tool.
 * Lets the store drive feature-state for undo/redo/plan-load without
 * importing maplibre or holding a map reference.
 */
export interface MapBridge {
  applyFeatureState(changes: Array<{ geoid: string; district: number | null }>): void;
  clearFeatureState(): void;
}

/** Cap for the undo stack (each entry is one stroke's diff). */
export const UNDO_DEPTH = 100;
/** Debounce window after the last stroke before the bulk upsert fires. */
export const SAVE_DEBOUNCE_MS = 1200;

interface PaintingState {
  /** Tenant slug this store's data belongs to; changes reset the store. */
  tenantSlug: string | null;
  planId: string | null;
  planName: string;
  planStatus: PlanStatus;
  /** True when the plan exists only in this session (backend 501 fallback). */
  isLocalDraft: boolean;
  districtCount: number;
  colors: string[];
  /** Display names: the plan's custom names over the jurisdiction's defaults. */
  labels: string[];
  /** The jurisdiction's default names for this district count. */
  defaultLabels: string[];
  /** The plan's own names, one per district ("" = use the default). */
  customLabels: string[];
  labelsState: CommentsState;
  labelsError: string | null;
  assignments: Record<string, number>;
  activeDistrict: number;
  paintMode: PaintMode;
  /** Brush radius step for paint/erase strokes. */
  brushSize: BrushSize;
  /**
   * When true, paint strokes only claim unassigned blocks (and blocks already
   * in the active district) — other districts' blocks are protected. Erase
   * likewise only clears the active district.
   */
  lockAssigned: boolean;

  populations: Record<string, number> | null;
  populationsState: PopulationsState;
  populationsError: string | null;
  totalPopulation: number;

  /** Plans known this session (backend list + locally created). */
  knownPlans: PlanSummary[];

  undoStack: StrokeDiff[];
  redoStack: StrokeDiff[];
  /** GEOIDs changed since the last successful save. */
  dirtyGeoids: string[];
  saveState: SaveState;
  saveError: string | null;
  lastSavedAt: string | null;

  bridge: MapBridge | null;

  /**
   * Latest server validation report for the active plan (POST
   * /v1/plans/{id}/validate). Null until the user validates. `validationStale`
   * flips true on any paint/undo/redo after the report was produced, so the
   * stats panel can label figures honestly instead of showing stale badges.
   */
  validationReport: ValidationReport | null;
  validationStale: boolean;

  setTenant: (slug: string, tenant: TenantConfig) => void;
  setBridge: (bridge: MapBridge | null) => void;
  setValidationReport: (report: ValidationReport | null) => void;
  setActiveDistrict: (district: number) => void;
  setPaintMode: (mode: PaintMode) => void;
  setBrushSize: (size: BrushSize) => void;
  setLockAssigned: (lock: boolean) => void;
  setPlanStatus: (status: PlanStatus) => void;
  /**
   * Turn the open guest draft into a real backend plan (after sign-in):
   * create it, upload every assignment, then reopen it from the server.
   * Resolves with the new plan id; throws (draft untouched) on failure.
   */
  promoteLocalDraft: (tenant: TenantConfig) => Promise<string>;
  /** Delete a guest draft from this browser (and close it if open). */
  discardLocalDraft: (id: string) => void;
  /**
   * Delete a plan: an account plan on the server, or a draft saved in this
   * browser. Closes it first if it's open (without saving pending edits).
   */
  removePlan: (id: string) => Promise<void>;  ensurePopulations: () => Promise<void>;
  loadPlan: (plan: PlanDetail, tenant: TenantConfig) => void;
  loadLocalDraft: (plan: PlanSummary, tenant: TenantConfig) => void;
  registerKnownPlan: (plan: PlanSummary) => void;
  unloadPlan: () => void;
  /** Apply a finished stroke: assignments, undo stack, dirty set, debounced save. */
  commitStroke: (changes: AssignmentChange[]) => void;
  undo: () => void;
  redo: () => void;
  /** Push the entire current assignment map through the bridge (map remounts). */
  repaintAll: () => void;
  saveNow: () => Promise<void>;

  /** The author's comments on the open plan (explanation for reviewers). */
  comments: string;
  commentsState: CommentsState;
  commentsError: string | null;
  /** Update the comments text; autosaves after a typing pause. */
  setComments: (text: string) => void;
  /** Save comments now (flushes the debounce). */
  saveComments: () => Promise<void>;
  /** Rename a district ("" restores the default); autosaves. */
  setDistrictLabel: (index: number, name: string) => void;
  /** Save district names now (flushes the debounce). */
  saveDistrictLabels: () => Promise<void>;
  retrySave: () => Promise<void>;
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
let commentsTimer: ReturnType<typeof setTimeout> | null = null;
let labelsTimer: ReturnType<typeof setTimeout> | null = null;

function clearLabelsTimer(): void {
  if (labelsTimer) {
    clearTimeout(labelsTimer);
    labelsTimer = null;
  }
}

/** Custom names padded/cut to the district count. */
function sizeLabels(custom: readonly string[] | null | undefined, count: number): string[] {
  return Array.from({ length: count }, (_, i) => custom?.[i] ?? '');
}

function clearCommentsTimer(): void {
  if (commentsTimer) {
    clearTimeout(commentsTimer);
    commentsTimer = null;
  }
}
let populationsPromise: Promise<void> | null = null;

function clearSaveTimer(): void {
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
}

function districtConfigFromTenant(tenant: TenantConfig, districtCount: number): {
  colors: string[];
  labels: string[];
} {
  return {
    colors: resolveDistrictColors(districtCount, tenant.districts.colors),
    labels: resolveDistrictLabels(districtCount, tenant.districts.labels),
  };
}

/** Ideal (target) population per district = tenant total / district count. */
export function selectIdealPopulation(state: PaintingState): number {
  return state.districtCount > 0 ? state.totalPopulation / state.districtCount : 0;
}

export const usePaintingStore = create<PaintingState>()((set, get) => {
  function scheduleSave(): void {
    clearSaveTimer();
    // Guest drafts save to localStorage: cheap and synchronous, so persist
    // every stroke at once — a reload right after painting loses nothing.
    if (get().isLocalDraft) {
      void get().saveNow();
      return;
    }
    saveTimer = setTimeout(() => {
      saveTimer = null;
      void get().saveNow();
    }, SAVE_DEBOUNCE_MS);
  }

  function applyChangesToAssignments(
    assignments: Record<string, number>,
    changes: AssignmentChange[],
    usePrev: boolean,
  ): Record<string, number> {
    const next = { ...assignments };
    for (const change of changes) {
      const value = usePrev ? change.prev : change.next;
      if (value === null) {
        delete next[change.geoid];
      } else {
        next[change.geoid] = value;
      }
    }
    return next;
  }

  function markDirty(state: PaintingState, changes: AssignmentChange[]): Partial<PaintingState> {
    const dirty = new Set(state.dirtyGeoids);
    for (const change of changes) dirty.add(change.geoid);
    return { dirtyGeoids: [...dirty] };
  }

  return {
    tenantSlug: null,
    planId: null,
    planName: '',
    planStatus: 'draft',
    isLocalDraft: false,
    districtCount: 0,
    colors: [],
    labels: [],
    defaultLabels: [],
    customLabels: [],
    labelsState: 'idle',
    labelsError: null,
    assignments: {},
    activeDistrict: 0,
    paintMode: 'paint',
    brushSize: 0,
    lockAssigned: false,

    populations: null,
    populationsState: 'idle',
    populationsError: null,
    totalPopulation: 0,

    knownPlans: [],

    undoStack: [],
    redoStack: [],
    dirtyGeoids: [],
    saveState: 'idle',
    saveError: null,
    lastSavedAt: null,

    bridge: null,

    comments: '',
    commentsState: 'idle',
    commentsError: null,

    validationReport: null,
    validationStale: false,

    setTenant: (slug, _tenant) => {
      if (get().tenantSlug === slug) return;
      clearSaveTimer();
      clearCommentsTimer();
      populationsPromise = null;
      set({
        tenantSlug: slug,
        planId: null,
        planName: '',
        planStatus: 'draft',
        isLocalDraft: false,
        districtCount: 0,
        colors: [],
        labels: [],
        defaultLabels: [],
        customLabels: [],
        labelsState: 'idle',
        labelsError: null,
        assignments: {},
        activeDistrict: 0,
        paintMode: 'paint',
        populations: null,
        populationsState: 'idle',
        populationsError: null,
        totalPopulation: 0,
        // Guest drafts saved in this browser for this tenant.
        knownPlans: listLocalDrafts(slug),
        comments: '',
        commentsState: 'idle',
        commentsError: null,
        undoStack: [],
        redoStack: [],
        dirtyGeoids: [],
        saveState: 'idle',
        saveError: null,
        lastSavedAt: null,
        validationReport: null,
        validationStale: false,
      });
    },

    setBridge: (bridge) => set({ bridge }),

    setValidationReport: (report) =>
      set({ validationReport: report, validationStale: false }),

    setActiveDistrict: (district) => {
      if (district >= 0 && district < get().districtCount) set({ activeDistrict: district });
    },

    setPaintMode: (mode) => set({ paintMode: mode }),

    setBrushSize: (size) => set({ brushSize: size }),

    setLockAssigned: (lock) => set({ lockAssigned: lock }),

    setPlanStatus: (status) =>
      set((state) => ({
        planStatus: status,
        knownPlans: state.knownPlans.map((p) =>
          p.id === state.planId ? { ...p, status } : p,
        ),
      })),

    ensurePopulations: () => {
      if (populationsPromise) return populationsPromise;
      const state = get();
      if (state.populationsState === 'ready' || state.populationsState === 'loading') {
        return Promise.resolve();
      }
      set({ populationsState: 'loading', populationsError: null });
      populationsPromise = fetchBlockPopulations()
        .then(({ geoids, populations }) => {
          const map: Record<string, number> = {};
          let total = 0;
          for (let i = 0; i < geoids.length; i += 1) {
            const geoid = geoids[i];
            const pop = populations[i] ?? 0;
            if (geoid !== undefined) {
              map[geoid] = pop;
              total += pop;
            }
          }
          set({ populations: map, populationsState: 'ready', totalPopulation: total });
        })
        .catch((error: unknown) => {
          const message =
            error instanceof Error ? error.message : 'Could not load block populations.';
          set({ populationsState: 'error', populationsError: message });
        })
        .finally(() => {
          populationsPromise = null;
        });
      return populationsPromise;
    },

    loadPlan: (plan, tenant) => {
      // Don't lose a comment typed just before switching plans.
      if (get().commentsState === 'pending') void get().saveComments();
      if (get().labelsState === 'pending') void get().saveDistrictLabels();
      clearSaveTimer();
      const districtCount = plan.district_count > 0 ? plan.district_count : tenant.districts.count;
      const { colors, labels: defaultLabels } = districtConfigFromTenant(tenant, districtCount);
      const customLabels = sizeLabels(plan.district_labels, districtCount);
      get().bridge?.clearFeatureState();
      get().bridge?.applyFeatureState(
        Object.entries(plan.assignments).map(([geoid, district]) => ({ geoid, district })),
      );
      set((state) => ({
        planId: plan.id,
        planName: plan.name,
        planStatus: plan.status,
        isLocalDraft: false,
        districtCount,
        colors,
        defaultLabels,
        customLabels,
        labels: mergeDistrictLabels(defaultLabels, customLabels),
        labelsState: 'idle',
        labelsError: null,
        assignments: { ...plan.assignments },
        comments: plan.comments ?? '',
        commentsState: 'idle',
        commentsError: null,
        activeDistrict: 0,
        undoStack: [],
        redoStack: [],
        dirtyGeoids: [],
        saveState: 'idle',
        saveError: null,
        lastSavedAt: null,
        validationReport: null,
        validationStale: false,
        knownPlans: state.knownPlans.some((p) => p.id === plan.id)
          ? state.knownPlans
          : [...state.knownPlans, plan],
      }));
    },

    loadLocalDraft: (plan, tenant) => {
      // Don't lose a comment typed just before switching plans.
      if (get().commentsState === 'pending') void get().saveComments();
      if (get().labelsState === 'pending') void get().saveDistrictLabels();
      clearSaveTimer();
      const districtCount = plan.district_count > 0 ? plan.district_count : tenant.districts.count;
      const { colors, labels: defaultLabels } = districtConfigFromTenant(tenant, districtCount);
      const stored = readLocalDraft(tenant.slug, plan.id);
      const customLabels = sizeLabels(stored?.district_labels, districtCount);
      const assignments = stored ? { ...stored.assignments } : {};
      get().bridge?.clearFeatureState();
      get().bridge?.applyFeatureState(
        Object.entries(assignments).map(([geoid, district]) => ({ geoid, district })),
      );
      // Persist immediately so a brand-new draft survives a reload.
      writeLocalDraft(tenant.slug, {
        id: plan.id,
        name: plan.name,
        district_count: districtCount,
        updated_at: stored?.updated_at ?? new Date().toISOString(),
        assignments,
        comments: stored?.comments ?? '',
        district_labels: customLabels,
      });
      set((state) => ({
        planId: plan.id,
        planName: plan.name,
        planStatus: plan.status,
        isLocalDraft: true,
        districtCount,
        colors,
        defaultLabels,
        customLabels,
        labels: mergeDistrictLabels(defaultLabels, customLabels),
        labelsState: 'idle',
        labelsError: null,
        assignments,
        comments: stored?.comments ?? '',
        commentsState: 'idle',
        commentsError: null,
        activeDistrict: 0,
        undoStack: [],
        redoStack: [],
        dirtyGeoids: [],
        saveState: 'idle',
        saveError: null,
        lastSavedAt: stored?.updated_at ?? null,
        validationReport: null,
        validationStale: false,
        knownPlans: state.knownPlans.some((p) => p.id === plan.id)
          ? state.knownPlans
          : [...state.knownPlans, plan],
      }));
    },

    registerKnownPlan: (plan) =>
      set((state) =>
        state.knownPlans.some((p) => p.id === plan.id)
          ? state
          : { knownPlans: [...state.knownPlans, plan] },
      ),

    unloadPlan: () => {
      // Don't lose a comment typed just before switching plans.
      if (get().commentsState === 'pending') void get().saveComments();
      if (get().labelsState === 'pending') void get().saveDistrictLabels();
      clearSaveTimer();
      clearCommentsTimer();
      clearLabelsTimer();
      get().bridge?.clearFeatureState();
      set({
        planId: null,
        customLabels: [],
        labelsState: 'idle',
        labelsError: null,
        planName: '',
        planStatus: 'draft',
        isLocalDraft: false,
        assignments: {},
        comments: '',
        commentsState: 'idle',
        commentsError: null,
        undoStack: [],
        redoStack: [],
        dirtyGeoids: [],
        saveState: 'idle',
        saveError: null,
        lastSavedAt: null,
        validationReport: null,
        validationStale: false,
      });
    },

    commitStroke: (changes) => {
      if (changes.length === 0) return;
      const state = get();
      const undoStack = [...state.undoStack, { changes }];
      if (undoStack.length > UNDO_DEPTH) undoStack.splice(0, undoStack.length - UNDO_DEPTH);
      set({
        assignments: applyChangesToAssignments(state.assignments, changes, false),
        undoStack,
        redoStack: [],
        validationStale: state.validationReport !== null,
        ...markDirty(state, changes),
      });
      scheduleSave();
    },

    undo: () => {
      const state = get();
      const last = state.undoStack[state.undoStack.length - 1];
      if (!last) return;
      state.bridge?.applyFeatureState(
        last.changes.map((c) => ({ geoid: c.geoid, district: c.prev })),
      );
      set({
        assignments: applyChangesToAssignments(state.assignments, last.changes, true),
        undoStack: state.undoStack.slice(0, -1),
        redoStack: [...state.redoStack, last],
        validationStale: state.validationReport !== null,
        ...markDirty(state, last.changes),
      });
      scheduleSave();
    },

    redo: () => {
      const state = get();
      const next = state.redoStack[state.redoStack.length - 1];
      if (!next) return;
      state.bridge?.applyFeatureState(
        next.changes.map((c) => ({ geoid: c.geoid, district: c.next })),
      );
      set({
        assignments: applyChangesToAssignments(state.assignments, next.changes, false),
        undoStack: [...state.undoStack, next],
        redoStack: state.redoStack.slice(0, -1),
        validationStale: state.validationReport !== null,
        ...markDirty(state, next.changes),
      });
      scheduleSave();
    },

    repaintAll: () => {
      const state = get();
      const bridge = state.bridge;
      if (!bridge) return;
      bridge.clearFeatureState();
      bridge.applyFeatureState(
        Object.entries(state.assignments).map(([geoid, district]) => ({ geoid, district })),
      );
    },

    saveNow: async () => {
      const state = get();
      if (!state.planId || state.dirtyGeoids.length === 0) return;
      if (state.saveState === 'saving') return;
      // Guest drafts save to this browser (localStorage), not the server.
      if (state.isLocalDraft) {
        const now = new Date().toISOString();
        const persisted = state.tenantSlug
          ? writeLocalDraft(state.tenantSlug, {
              id: state.planId,
              name: state.planName,
              district_count: state.districtCount,
              updated_at: now,
              assignments: state.assignments,
              comments: state.comments,
              district_labels: state.customLabels,
            })
          : false;
        if (persisted) {
          set({ dirtyGeoids: [], saveState: 'idle', saveError: null, lastSavedAt: now });
        } else {
          set({
            saveState: 'error',
            saveError:
              'This browser is blocking storage, so your guest draft lives only in this tab. ' +
              'Sign in to save it to an account.',
          });
        }
        return;
      }
      set({ saveState: 'saving', saveError: null });
      const dirty = state.dirtyGeoids;
      // Null values un-assign blocks (the backend deletes those rows).
      const payload: Record<string, number | null> = {};
      for (const geoid of dirty) {
        const district = state.assignments[geoid];
        payload[geoid] = district === undefined ? null : district;
      }
      try {
        await putAssignments(state.planId, payload);
        // Only clear the geoids this save actually sent; strokes during the
        // save re-dirtied their own entries.
        const sent = new Set(dirty);
        const remaining = get().dirtyGeoids.filter((g) => !sent.has(g));
        set({
          dirtyGeoids: remaining,
          saveState: 'idle',
          saveError: null,
          lastSavedAt: new Date().toISOString(),
        });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Save failed for an unknown reason.';
        set({
          saveState: 'error',
          saveError: `Could not save ${dirty.length.toLocaleString('en-US')} changed block(s): ${message} — your work is preserved locally. Retry when ready.`,
        });
      }
    },

    retrySave: async () => {
      await get().saveNow();
    },

    setComments: (text) => {
      set({ comments: text, commentsState: 'pending', commentsError: null });
      clearCommentsTimer();
      commentsTimer = setTimeout(() => {
        commentsTimer = null;
        void get().saveComments();
      }, COMMENTS_SAVE_DEBOUNCE_MS);
    },

    saveComments: async () => {
      clearCommentsTimer();
      const state = get();
      if (!state.planId || state.commentsState === 'idle' || state.commentsState === 'saved') return;
      const planId = state.planId;
      const text = state.comments;
      if (state.isLocalDraft) {
        const stored = state.tenantSlug ? readLocalDraft(state.tenantSlug, planId) : null;
        const ok = state.tenantSlug
          ? writeLocalDraft(state.tenantSlug, {
              id: planId,
              name: state.planName,
              district_count: state.districtCount,
              updated_at: new Date().toISOString(),
              assignments: stored?.assignments ?? state.assignments,
              comments: text,
              district_labels: state.customLabels,
            })
          : false;
        set(ok ? { commentsState: 'saved' } : { commentsState: 'error', commentsError: 'This browser is blocking storage.' });
        return;
      }
      set({ commentsState: 'saving' });
      try {
        await putPlanComments(planId, text);
        // Ignore the result if the plan changed or the user kept typing.
        if (get().planId === planId && get().comments === text) set({ commentsState: 'saved' });
      } catch (error) {
        if (get().planId === planId) {
          set({
            commentsState: 'error',
            commentsError: error instanceof Error ? error.message : 'Could not save comments.',
          });
        }
      }
    },

    setDistrictLabel: (index, name) => {
      const state = get();
      if (!state.planId || index < 0 || index >= state.districtCount) return;
      const customLabels = sizeLabels(state.customLabels, state.districtCount);
      customLabels[index] = name.replace(/\s+/g, ' ').slice(0, DISTRICT_LABEL_MAX);
      set({
        customLabels,
        labels: mergeDistrictLabels(state.defaultLabels, customLabels),
        labelsState: 'pending',
        labelsError: null,
      });
      clearLabelsTimer();
      labelsTimer = setTimeout(() => {
        labelsTimer = null;
        void get().saveDistrictLabels();
      }, COMMENTS_SAVE_DEBOUNCE_MS);
    },

    saveDistrictLabels: async () => {
      clearLabelsTimer();
      const state = get();
      if (!state.planId || state.labelsState !== 'pending') return;
      const planId = state.planId;
      const labels = state.customLabels.map((l) => l.trim());
      if (state.isLocalDraft) {
        const stored = state.tenantSlug ? readLocalDraft(state.tenantSlug, planId) : null;
        const ok = state.tenantSlug
          ? writeLocalDraft(state.tenantSlug, {
              id: planId,
              name: state.planName,
              district_count: state.districtCount,
              updated_at: new Date().toISOString(),
              assignments: stored?.assignments ?? state.assignments,
              comments: stored?.comments ?? state.comments,
              district_labels: labels,
            })
          : false;
        set(ok ? { labelsState: 'saved' } : { labelsState: 'error', labelsError: 'This browser is blocking storage.' });
        return;
      }
      set({ labelsState: 'saving' });
      try {
        await putDistrictLabels(planId, labels);
        const now = get();
        if (now.planId === planId && now.customLabels.map((l) => l.trim()).join('\u0000') === labels.join('\u0000')) {
          set({ labelsState: 'saved' });
        }
      } catch (error) {
        if (get().planId === planId) {
          set({
            labelsState: 'error',
            labelsError: error instanceof Error ? error.message : 'Could not save district names.',
          });
        }
      }
    },

    promoteLocalDraft: async (tenant) => {
      const state = get();
      if (!state.planId || !state.isLocalDraft) throw new Error('No guest draft is open.');
      const localId = state.planId;
      const { planName, districtCount } = state;
      const assignments = { ...state.assignments };
      const summary = await createPlan(planName || 'Untitled plan', districtCount);
      if (Object.keys(assignments).length > 0) {
        await putAssignments(summary.id, assignments);
      }
      if (state.comments.trim()) {
        await putPlanComments(summary.id, state.comments);
      }
      if (state.customLabels.some((l) => l.trim())) {
        await putDistrictLabels(summary.id, state.customLabels);
      }
      const detail = await getPlan(summary.id, districtCount, tenant.districts.labels);
      if (state.tenantSlug) deleteLocalDraft(state.tenantSlug, localId);
      set((s) => ({ knownPlans: s.knownPlans.filter((p) => p.id !== localId) }));
      get().loadPlan(detail, tenant);
      return detail.id;
    },

    discardLocalDraft: (id) => {
      const state = get();
      if (!isLocalPlanId(id)) return;
      if (state.tenantSlug) deleteLocalDraft(state.tenantSlug, id);
      if (state.planId === id) get().unloadPlan();
      set((s) => ({ knownPlans: s.knownPlans.filter((p) => p.id !== id) }));
    },

    removePlan: async (id) => {
      if (isLocalPlanId(id)) {
        get().discardLocalDraft(id);
        return;
      }
      const isOpen = get().planId === id;
      // Stop pending autosaves so nothing is written to a deleted plan.
      if (isOpen) {
        clearSaveTimer();
        clearCommentsTimer();
        clearLabelsTimer();
      }
      // If this fails the plan stays open; unsaved edits remain in the dirty
      // set and save with the next change.
      await deletePlan(id);
      if (get().planId === id) {
        set({ commentsState: 'idle', labelsState: 'idle', dirtyGeoids: [] });
        get().unloadPlan();
      }
      set((s) => ({ knownPlans: s.knownPlans.filter((p) => p.id !== id) }));
    },
  };
});

if (import.meta.env.DEV) (window as unknown as { __rdStore?: typeof usePaintingStore }).__rdStore = usePaintingStore;
